import { Platform, type View } from "@-/com.npmjs.react-native";
import Animated, {
  measure,
  useAnimatedRef,
  useAnimatedStyle,
  useSharedValue,
  type SharedValue,
} from "@-/com.npmjs.react-native-reanimated";
import { type ReactNode, useContext, useState, useCallback } from "react";
import {
  ToolbarSheetShadowSourceContext,
  ToolbarSheetShadowPublisherContext,
  type ToolbarSheetShadowSource,
  isToolbarSheetShadowHidden,
} from "#toolbar-sheet-shadow-context";
export function ToolbarSheetShadowProvider({ children }: { children: ReactNode }) {
  const [source, setSource] = useState<ToolbarSheetShadowSource | null>(null);
  const publish = useCallback((next: ToolbarSheetShadowSource) => {
    setSource(next);
    return () => setSource((current) => (current === next ? null : current));
  }, []);
  return (
    <ToolbarSheetShadowPublisherContext value={Platform.OS === "web" ? null : publish}>
      <ToolbarSheetShadowSourceContext value={source}>{children}</ToolbarSheetShadowSourceContext>
    </ToolbarSheetShadowPublisherContext>
  );
}

/** A noninteractive shell sibling above the header, below the app's modal hosts. */
export function ToolbarSheetShadow({
  drawerProgress,
  hidden = false,
}: {
  drawerProgress?: SharedValue<number>;
  hidden?: boolean;
}) {
  const source = useContext(ToolbarSheetShadowSourceContext);
  const hostRef = useAnimatedRef<View>();
  const layoutRevision = useSharedValue(0);
  const shadowStyle = useAnimatedStyle(() => {
    // A first frame can precede host layout. Re-evaluate even if the sheet is already at rest.
    layoutRevision.get();
    const frame = source?.get();
    const host = measure(hostRef);
    if (
      frame == null ||
      host === null ||
      isToolbarSheetShadowHidden(hidden, drawerProgress?.get() ?? 0)
    ) {
      return { opacity: 0 };
    }
    return {
      height: frame.height,
      opacity: 1,
      transform: [{ translateX: frame.x - host.pageX }, { translateY: frame.y - host.pageY }],
      width: frame.width,
    };
  });
  return (
    <Animated.View
      accessibilityElementsHidden
      className="absolute inset-0 z-60 overflow-visible"
      collapsable={false}
      importantForAccessibility="no-hide-descendants"
      onLayout={() => layoutRevision.set((value) => value + 1)}
      pointerEvents="none"
      ref={hostRef}
      testID="toolbar-sheet-shadow-host"
    >
      {/* An outer box shadow needs no filled surface. Leaving the box transparent keeps the
          sheet's wallet/handle/content and the navigation tabs beneath it visible. */}
      <Animated.View
        // oxlint-disable-next-line tailwindcss/no-restricted-classes -- Matches the sheet's platform-style 28pt exposed corners.
        className="absolute top-0 left-0 rounded-t-[28px] shadow-[0_-2px_16px] shadow-black/16"
        pointerEvents="none"
        style={shadowStyle}
        testID="toolbar-sheet-shadow"
      />
    </Animated.View>
  );
}
